import React from 'react';
import ReactDOM from "react-dom";
import MaterialTable from 'material-table';
import { createMuiTheme, MuiThemeProvider, withStyles } from '@material-ui/core/styles';
import { ATTACHABLES } from '../constants/AttachmentConstants';
import AttachmentDataForm from './AttachmentDataForm';
import AttachmentDataRemoval from './AttachmentDataRemoval';
import {
  renderContext, renderDelivery, renderDocument, renderFile, renderLink, renderPatient, renderType,
} from './attachments/AttachmentCells';

const HEADER_STYLE = {
  backgroundColor: 'var(--theme-surface-2, #f8f9fa)',
  color: 'var(--theme-text-muted, #636e72)',
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  whiteSpace: 'nowrap',
};

// Row hover highlight; the soft-dark theme already defines --theme-hover.
const TableShell = withStyles({
  root: {
    '& .MuiTableBody-root .MuiTableRow-root:hover .MuiTableCell-root': {
      backgroundColor: 'var(--theme-hover, rgba(0, 0, 0, 0.04))',
    },
  },
})(({ classes, children }) => <div className={classes.root}>{children}</div>);

export default class AttachmentDataTable extends React.Component {
  constructor(props) {
    super(props);

    this.tableRef = React.createRef();

    this.theme = createMuiTheme({
      palette: {
        primary: {
          main: '#7cb342',
        },
        secondary: {
          main: '#4caf50',
        },
      },
    });

    this.BASE_URL = '/api/attachments';

    // Arrange colums
    const baseColumns = [
      // Add sorting and order field and grouping
      // Composed columns (Patient, Document, Context) carry EHC and Afya fields side by side,
      // so they only sort/group where the field maps to a single JSON key.
      { title: 'Type', field: 'attachable_type', editable: 'never', render: renderType },
      { title: 'Patient', field: 'profile_full_name', editable: 'never', grouping: false, render: renderPatient },
      { title: 'Document', field: 'attachment_number', editable: 'never', grouping: false, sorting: false, render: renderDocument },
      { title: 'Context', field: 'doctor_full_name', editable: 'never', grouping: false, sorting: false, render: renderContext },
      {
        title: 'Hospital', field: 'hospital_name', editable: 'never',
        render: rowData => (
          <a href={this.tryGetRowDataField(rowData, 'hospital_url')}>
            {this.tryGetRowDataField(rowData, 'hospital_name')}
          </a>
        ),
      },
      { title: 'Delivery', field: 'medical_result.whatsapp_status', editable: 'never', sorting: false, render: renderDelivery },
      { title: 'Link', field: 'medical_result.share_state', editable: 'never', sorting: false, render: renderLink },
      { title: 'File', field: 'file_url', editable: 'never', grouping: false, sorting: false, export: false, render: renderFile },
      { title: 'Created at', field: 'created_at', editable: 'never', grouping: false },
    ];

    // Arrange actions
    let baseActions = [
      {
        icon: 'refresh',
        tooltip: 'Refresh Data',
        isFreeAction: true,
        onClick: () =>
          this.tableRef.current && this.tableRef.current.onQueryChange(),
      },
    ]

    // When editable
    if (this.props.editable) {
      baseActions.push({
        icon: 'add',
        tooltip: 'Add',
        isFreeAction: true,
        onClick: () => this.handleOpenAttachmentFormModal({attachable_type: ATTACHABLES.EHC_SERVICE_ORDER})
      });
      // Afya rows are written by hermina-core; the form cannot represent them.
      // Kept as an object with an explicit `position`: with `selection` on,
      // material-table re-maps bare function actions to the selection toolbar.
      baseActions.push({
        position: 'row',
        action: rowData => ({
          icon: 'edit',
          tooltip: 'Edit',
          hidden: rowData.attachable_type === ATTACHABLES.AFYA_MEDICAL_RESULT,
          onClick: (evt, selectedRow) => this.handleOpenAttachmentFormModal(selectedRow)
        })
      });
      baseActions.push({
        icon: 'delete',
        tooltip: 'Delete',
        position: 'row',
        onClick: (evt, selectedRow) => this.handleOpenAttachmentRemovalModal([selectedRow])
      });
      baseActions.push({
        icon: 'delete',
        tooltip: 'Batch Delete',
        onClick: (evt, selectedRows) => this.handleOpenAttachmentRemovalModal(selectedRows)
      });
    }

    // This function to try to get a nested field from an object
    // Usefull for custom render of Material Table that combined with grouping,
    // When grouping, custom render is used for displaying the data the param that passed is the field directly
    // It is assuming the fields of the data is not nested
    this.tryGetRowDataField = (input, field) => {
      if (input !== null && typeof input === "object") {
        return input[field];
      }
      return input;
    }

    this.state = {
      actions: baseActions,
      columns: baseColumns,
      data: [],
      page: null,
      totalCount: null,

      // Modal state
      attachmentFormModalOpen: false,
      employeeOrderConfirmationModalOpen: false,
      employeeOrdersCompletionModalOpen: false,
      attachmentRemovalModalOpen: false,
      selectedAttachments: null,
      editedAttachment: null
    }
  }

  // =====================
  // Modal Handlers
  // =====================
  handleOpenAttachmentFormModal = async (attachment) => {
    this.setState({
      attachmentFormModalOpen: true,
      attachmentRemovalModalOpen: false,
      selectedAttachments: null,
      editedAttachment: attachment
    });
  };

  handleOpenAttachmentRemovalModal = (attachments) => {
    this.setState({
      attachmentFormModalOpen: false,
      attachmentRemovalModalOpen: true,
      selectedAttachments: attachments,
      editedAttachment: null
    });
  };

  handleCloseModal = () => {
    this.setState({
      attachmentFormModalOpen: false,
      attachmentRemovalModalOpen: false,
      selectedAttachments: null,
      editedAttachment: null
    });
  };

  // =====================
  // Render Modal
  // =====================
  renderModals() {
    return <>
      {this.props.editable &&
        <AttachmentDataForm
          attachment={this.state.editedAttachment}
          hospitals={this.props.hospitals}
          profiles={this.props.profiles}
          editable={this.props.editable}
          removable={this.props.editable}
          tableRef={this.tableRef}
          open={this.state.attachmentFormModalOpen}
          onClose={this.handleCloseModal}
          onRemove={() => this.handleOpenAttachmentRemovalModal([this.state.editedAttachment])}
        ></AttachmentDataForm>
      }

      {this.props.editable &&
        <AttachmentDataRemoval
          attachments={this.state.selectedAttachments}
          removable={this.props.editable}
          tableRef={this.tableRef}
          open={this.state.attachmentRemovalModalOpen}
          onClose={this.handleCloseModal}
        ></AttachmentDataRemoval>
      }
    </>
  }

  // =====================
  // Render Component
  // =====================
  render () {
    return (
      <MuiThemeProvider theme={this.theme}>

        {this.renderModals()}

        <TableShell>
        <MaterialTable
          title="Attachments"
          tableRef={this.tableRef}
          columns={this.state.columns}
          data={query =>
            new Promise((resolve, reject) => {
              let url = this.BASE_URL + '?'
              url += 'per_page=' + query.pageSize
              url += '&q=' + query.search
              url += '&page=' + (query.page + 1)
              if (this.props.profiles) {
                url += '&profile_ids=' + (this.props.profiles.map(([_, id]) => id).join(','))
              }
              fetch(url)
                .then(response => response.json())
                .then(result => {
                  resolve({
                    data: result.attachments,
                    page: result.pagination.page - 1,
                    totalCount: result.pagination.count,
                  })
                })
            })
          }
          actions={this.state.actions}
          localization={{
            body: {
              emptyDataSourceMessage: (
                <span>
                  No attachments found
                  <br />
                  <small>Uploaded files and Afya medical results appear here.</small>
                </span>
              ),
            },
          }}
          options={{
            pageSize: 10,
            pageSizeOptions: [10, 100, 200, 300, 500],
            exportButton: true,
            grouping: true,
            sorting: true,
            search: true,
            selection: this.props.editable,
            padding: 'dense',
            headerStyle: HEADER_STYLE,
          }}
          // onRowClick={(event, rowData) => Turbolinks.visit(rowData.attachment_url)}
        />
        </TableShell>
      </MuiThemeProvider>
    );
  }
}

